import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { motion } from "framer-motion";
import {
  Ticket,
  Bell,
  BarChart3,
  Shield,
  Clock,
  CheckCircle,
  ArrowRight,
  Zap,
  MessageSquare,
  Users,
  ChevronRight,
  Star,
  Send,
  Layers,
  HeadphonesIcon,
} from "lucide-react";
import logo from "../assets/logo.svg";
import ThemeToggle from "../components/ThemeToggle";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" },
  }),
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

export default function LandingPage() {
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: Ticket,
      title: "Smart Ticketing",
      description:
        "Submit and track IT support requests with an intuitive interface. Automatic categorization and priority assignment.",
      color: "text-blue-400",
      bgColor: "bg-blue-500/10",
      borderColor: "border-blue-500/20",
    },
    {
      icon: Bell,
      title: "Real-Time Notifications",
      description:
        "Stay updated with instant notifications on ticket progress, comments, and resolutions via email and in-app alerts.",
      color: "text-purple-400",
      bgColor: "bg-purple-500/10",
      borderColor: "border-purple-500/20",
    },
    {
      icon: MessageSquare,
      title: "Collaborative Resolution",
      description:
        "Communicate directly with IT staff through ticket comments. Share files, screenshots, and detailed descriptions.",
      color: "text-green-400",
      bgColor: "bg-green-500/10",
      borderColor: "border-green-500/20",
    },
    {
      icon: Clock,
      title: "SLA Tracking",
      description:
        "Every ticket is monitored against service level agreements ensuring timely responses and resolutions.",
      color: "text-yellow-400",
      bgColor: "bg-yellow-500/10",
      borderColor: "border-yellow-500/20",
    },
    {
      icon: BarChart3,
      title: "Analytics Dashboard",
      description:
        "Comprehensive dashboards with real-time metrics, resolution rates, and performance insights at a glance.",
      color: "text-cyan-400",
      bgColor: "bg-cyan-500/10",
      borderColor: "border-cyan-500/20",
    },
    {
      icon: Shield,
      title: "Secure & Reliable",
      description:
        "Enterprise-grade security with role-based access control. Your data is encrypted and protected at all times.",
      color: "text-red-400",
      bgColor: "bg-red-500/10",
      borderColor: "border-red-500/20",
    },
  ];

  const steps = [
    {
      step: "01",
      icon: Send,
      title: "Submit a Request",
      description:
        "Describe your issue, select a category, and set the priority. Attach screenshots or files for context.",
    },
    {
      step: "02",
      icon: Users,
      title: "Get Assigned",
      description:
        "Your ticket is automatically routed to the right technician based on category and workload.",
    },
    {
      step: "03",
      icon: CheckCircle,
      title: "Track & Resolve",
      description:
        "Follow real-time updates, communicate with your assigned technician, and get notified when it's resolved.",
    },
  ];

  const stats = [
    { value: "99.9%", label: "Uptime", icon: Zap },
    { value: "< 2h", label: "Avg. First Response", icon: Clock },
    { value: "95%", label: "Satisfaction Rate", icon: Star },
    { value: "24/7", label: "Support Available", icon: HeadphonesIcon },
  ];

  const faqs = [
    {
      question: "How do I submit a support ticket?",
      answer:
        "Simply sign in with your campus email, navigate to 'New Ticket' from the dashboard, fill in the details of your issue, and submit. You'll receive a confirmation email with your ticket number.",
    },
    {
      question: "How long does it take to get a response?",
      answer:
        "Most tickets receive an initial response within 2 hours during business hours. Critical issues are prioritized and addressed immediately.",
    },
    {
      question: "Can I track the progress of my ticket?",
      answer:
        "Yes! You can view real-time status updates, add comments, and receive email notifications at every stage of your ticket's lifecycle.",
    },
    {
      question: "What types of issues can I report?",
      answer:
        "You can report hardware issues, software problems, network connectivity, account access, email issues, and any other IT-related concerns.",
    },
    {
      question: "Who can use CamPulse?",
      answer:
        "CamPulse is available to all students, faculty, and staff with a valid campus email address.",
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <div className="fixed inset-0 bg-[linear-gradient(to_right,#16181d_1px,transparent_1px),linear-gradient(to_bottom,#16181d_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)] dark:opacity-100 opacity-[0.04]" />

      <nav className="z-50 border-b border-border/30 bg-background/80 backdrop-blur-xl sticky top-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 blur-xl animate-pulse" />
                <div className="relative w-10 h-10 flex items-center justify-center">
                  <img src={logo} alt="CamPulse logo" className="w-10 h-10" />
                </div>
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground tracking-tight">
                  <span className="text-primary font-semibold">Cam</span>
                  <span className="font-extralight">Pulse</span>
                </h1>
              </div>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <a
                href="#features"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Features
              </a>
              <a
                href="#how-it-works"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                How It Works
              </a>
              <a
                href="#faq"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                FAQ
              </a>
            </div>

            <div className="flex items-center gap-3">
              <ThemeToggle />
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg transition-all shadow-lg shadow-primary/25 text-sm"
                >
                  Dashboard
                  <ArrowRight className="w-4 h-4" />
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg transition-all shadow-lg shadow-primary/25 text-sm"
                >
                  Sign In
                  <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      <section className="relative z-10 pt-20 sm:pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-4xl mx-auto"
          >
            <motion.div
              custom={0}
              variants={fadeUp}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-sm text-primary mb-8"
            >
              <Layers className="w-4 h-4" />
              Real-Time Campus IT Service Management
            </motion.div>

            <motion.h1
              custom={1}
              variants={fadeUp}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 leading-[1.1]"
            >
              The Heartbeat of{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-blue-400 to-cyan-400">
                Campus IT
              </span>
            </motion.h1>

            <motion.p
              custom={2}
              variants={fadeUp}
              className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed"
            >
              Submit, track, and resolve IT support requests in one place.
              Designed for students, powered by your campus IT team.
            </motion.p>

            <motion.div
              custom={3}
              variants={fadeUp}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Link
                to={isAuthenticated ? "/dashboard" : "/login"}
                className="flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary/90 text-white font-bold rounded-xl transition-all shadow-xl shadow-primary/25 text-base group"
              >
                {isAuthenticated ? "Go to Dashboard" : "Get Started"}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="#how-it-works"
                className="flex items-center gap-2 px-8 py-4 bg-card border border-border/50 hover:border-primary/30 text-foreground font-semibold rounded-xl transition-all text-base"
              >
                Learn More
                <ChevronRight className="w-5 h-5" />
              </a>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto"
          >
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <div
                  key={idx}
                  className="bg-card/60 backdrop-blur-sm border border-border/30 rounded-xl p-5 text-center hover:border-primary/30 transition-all group"
                >
                  <Icon className="w-5 h-5 text-primary mx-auto mb-2 group-hover:scale-110 transition-transform" />
                  <div className="text-2xl sm:text-3xl font-bold text-foreground font-mono">
                    {stat.value}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section
        id="features"
        className="relative z-10 py-20 sm:py-28 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div
              custom={0}
              variants={fadeUp}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-sm text-primary mb-6"
            >
              <Zap className="w-4 h-4" />
              Features
            </motion.div>
            <motion.h2
              custom={1}
              variants={fadeUp}
              className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4"
            >
              Everything You Need
            </motion.h2>
            <motion.p
              custom={2}
              variants={fadeUp}
              className="text-lg text-muted-foreground max-w-2xl mx-auto"
            >
              A complete IT service management platform built for the campus
              experience
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={idx}
                  custom={idx}
                  variants={fadeUp}
                  className={`group bg-card/60 backdrop-blur-sm border ${feature.borderColor} rounded-2xl p-6 sm:p-8 hover:border-primary/40 transition-all hover:-translate-y-1`}
                >
                  <div
                    className={`inline-flex p-3 ${feature.bgColor} rounded-xl mb-5`}
                  >
                    <Icon className={`w-6 h-6 ${feature.color}`} />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section
        id="how-it-works"
        className="relative z-10 py-20 sm:py-28 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div
              custom={0}
              variants={fadeUp}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-sm text-primary mb-6"
            >
              <Layers className="w-4 h-4" />
              How It Works
            </motion.div>
            <motion.h2
              custom={1}
              variants={fadeUp}
              className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4"
            >
              Three Simple Steps
            </motion.h2>
            <motion.p
              custom={2}
              variants={fadeUp}
              className="text-lg text-muted-foreground max-w-2xl mx-auto"
            >
              From issue to resolution. Fast, transparent, and hassle-free
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={idx}
                  custom={idx}
                  variants={fadeUp}
                  className="relative text-center group"
                >
                  {idx < steps.length - 1 && (
                    <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-px border-t-2 border-dashed border-border/50" />
                  )}
                  <div className="relative inline-flex items-center justify-center w-24 h-24 bg-card border border-border/50 rounded-2xl mb-6 group-hover:border-primary/40 transition-all">
                    <Icon className="w-10 h-10 text-primary" />
                    <span className="absolute -top-3 -right-3 w-8 h-8 bg-primary text-white text-sm font-bold rounded-lg flex items-center justify-center shadow-lg">
                      {step.step}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">
                    {step.description}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section
        id="faq"
        className="relative z-10 py-20 sm:py-28 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div
              custom={0}
              variants={fadeUp}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-sm text-primary mb-6"
            >
              <MessageSquare className="w-4 h-4" />
              FAQ
            </motion.div>
            <motion.h2
              custom={1}
              variants={fadeUp}
              className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4"
            >
              Common Questions
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="space-y-4"
          >
            {faqs.map((faq, idx) => (
              <motion.details
                key={idx}
                custom={idx}
                variants={fadeUp}
                className="group bg-card/60 backdrop-blur-sm border border-border/30 rounded-xl overflow-hidden hover:border-primary/30 transition-all"
              >
                <summary className="flex items-center justify-between p-5 sm:p-6 cursor-pointer list-none">
                  <span className="font-semibold text-foreground pr-4">
                    {faq.question}
                  </span>
                  <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform group-open:rotate-90" />
                </summary>
                <div className="px-5 sm:px-6 pb-5 sm:pb-6 pt-0">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </motion.details>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="relative z-10 py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative bg-card border border-border/50 rounded-3xl p-8 sm:p-12 md:p-16 text-center overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-cyan-500/5" />
            <div className="relative z-10">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4">
                Ready to Get Started?
              </h2>
              <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">
                Sign in with your campus credentials and submit your first
                support request in under a minute.
              </p>
              <Link
                to={isAuthenticated ? "/dashboard" : "/login"}
                className="inline-flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary/90 text-white font-bold rounded-xl transition-all shadow-xl shadow-primary/25 text-base group"
              >
                {isAuthenticated ? "Go to Dashboard" : "Sign In Now"}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-border/30 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center">
              <img src={logo} alt="CamPulse logo" className="w-8 h-8" />
            </div>
            <span className="text-sm text-muted-foreground">
              <span className="text-primary font-semibold">Cam</span>
              <span className="font-extralight">Pulse</span>
              <span className="ml-2">
                © {new Date().getFullYear()} Campus IT Services
              </span>
            </span>
          </div>
          <div className="flex items-center gap-6">
            <a
              href="#features"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Features
            </a>
            <a
              href="#how-it-works"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              How It Works
            </a>
            <a
              href="#faq"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              FAQ
            </a>
            <Link
              to="/login"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Sign In
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
